import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Breadcrumb, Button, Form, Spinner } from "react-bootstrap";
import LoggedInLayout from "../../layouts/loggedin-layout";
import useUserStore from "../../stores/user-store";
import { hasAnyPermission, hasPermission, permissionPaths } from "../../auth/permission-paths";
import { getApiErrorMessages } from "../../new/api-errors";
import { useEmailNotificationsQuery, useUpdateEmailNotification } from "../../new/emailnotifications.query";
import styles from "../../styles/emailnotifications.module.css";

const deliveryTypes = [{ value: "To", label: "To" }, { value: "Cc", label: "CC" }, { value: "Bcc", label: "BCC" }];
const maxRecipients = 100;

const userLabel = (user, userId) => {
    if (!user) return `Unavailable user (${userId})`;
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username;
    return `${name}${user.username && user.username !== name ? ` (${user.username})` : ""}${user.email ? ` — ${user.email}` : ""}`;
};

const recipientPayload = (recipients) => recipients.map(({ deliveryType, source, userId, propertyName, audienceMembershipId, applicationStatusTypeId }) => ({
    deliveryType,
    source,
    userId: source === "User" ? userId ?? null : null,
    propertyName: source === "ApplicationProperty" ? propertyName ?? null : null,
    audienceMembershipId: source === "ListMembership" ? audienceMembershipId ?? null : null,
    applicationStatusTypeId: applicationStatusTypeId ?? null
}));

export const EmailEventCard = ({ event, users, listMemberships = [], canManage }) => {
    const [draft, setDraft] = useState(null);
    const [errorText, setErrorText] = useState("");
    const [successText, setSuccessText] = useState("");
    const nextRowId = useRef(0);
    const updateMutation = useUpdateEmailNotification();
    const usersById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);
    const eligibleUsers = useMemo(() => users.filter((user) => user.isEligible)
        .sort((a, b) => userLabel(a).localeCompare(userLabel(b))), [users]);
    const applicationStatuses = event.applicationStatuses ?? [];
    const isEditing = Boolean(draft) && canManage;
    const recipients = isEditing ? draft.recipients : event.recipients;
    const hasRemoteChanges = isEditing && draft.concurrencyToken !== event.concurrencyToken;
    const isDirty = isEditing && JSON.stringify(recipientPayload(draft.recipients)) !== JSON.stringify(draft.savedRecipients);

    const startEditing = () => {
        setDraft({
            concurrencyToken: event.concurrencyToken,
            savedRecipients: recipientPayload(event.recipients),
            recipients: recipientPayload(event.recipients).map((recipient) => ({ ...recipient, rowId: nextRowId.current++ }))
        });
        setErrorText("");
        setSuccessText("");
    };

    const changeRecipient = (rowId, patch) => {
        setDraft((previous) => ({
            ...previous,
            recipients: previous.recipients.map((recipient) => recipient.rowId === rowId ? { ...recipient, ...patch } : recipient)
        }));
        setErrorText("");
    };

    const addRecipient = () => {
        if (draft.recipients.length >= maxRecipients) return;
        const recipient = {
            rowId: nextRowId.current++,
            deliveryType: "To",
            source: event.dynamicRecipients.length ? "ApplicationProperty" : "User",
            userId: null,
            propertyName: null,
            audienceMembershipId: null,
            applicationStatusTypeId: null
        };
        setDraft((previous) => ({ ...previous, recipients: [...previous.recipients, recipient] }));
        setErrorText("");
    };

    const save = async (formEvent) => {
        formEvent.preventDefault();
        if (!canManage || !draft || updateMutation.isPending || hasRemoteChanges) return;

        const payload = recipientPayload(draft.recipients);
        const targets = new Set();
        for (const recipient of payload) {
            if ((recipient.source === "User" && !recipient.userId) ||
                (recipient.source === "ApplicationProperty" && !event.dynamicRecipients.some((property) => property.key === recipient.propertyName)) ||
                (recipient.source === "ListMembership" && !listMemberships.some((membership) => membership.id === recipient.audienceMembershipId)) ||
                (recipient.source === "ActingUser" && !event.supportsActingUser)) {
                setErrorText("Choose a recipient for every row, or remove the empty rows.");
                return;
            }
            if (recipient.applicationStatusTypeId && (!event.supportsApplicationStatusFilter ||
                !applicationStatuses.some((status) => status.id === recipient.applicationStatusTypeId))) {
                setErrorText("Choose an available application status or All statuses for every row.");
                return;
            }
            if (recipient.source === "User" && !usersById.get(recipient.userId)?.isEligible &&
                !draft.savedRecipients.some((saved) => saved.source === "User" && saved.userId === recipient.userId &&
                    saved.deliveryType === recipient.deliveryType && saved.applicationStatusTypeId === recipient.applicationStatusTypeId)) {
                setErrorText("This user is no longer eligible for automated emails. Choose another user or remove the recipient.");
                return;
            }
            const target = JSON.stringify([recipient.source, recipient.userId, recipient.propertyName, recipient.audienceMembershipId, recipient.applicationStatusTypeId]);
            if (targets.has(target)) {
                setErrorText("Each recipient can be assigned only once for the same status selection. Remove the duplicate or choose a different recipient or status.");
                return;
            }
            targets.add(target);
        }

        setErrorText("");
        try {
            await updateMutation.mutateAsync({ eventId: event.eventId, concurrencyToken: draft.concurrencyToken, recipients: payload });
            setDraft(null);
            setSuccessText(payload.length ? "Recipients saved." : "Recipients saved. Emails for this event are disabled.");
        } catch (error) {
            setErrorText((error?.status ?? error?.response?.status) === 409
                ? "Another administrator changed these recipients. Your edits are still here. Cancel and reopen the editor to load the saved recipients."
                : getApiErrorMessages(error, "Unable to save recipients.").join(" "));
        }
    };

    const recipientDescription = (recipient) => {
        switch (recipient.source) {
            case "User": return userLabel(usersById.get(recipient.userId), recipient.userId);
            case "ApplicationProperty": return event.dynamicRecipients.find((property) => property.key === recipient.propertyName)?.label ?? recipient.propertyName;
            case "ListMembership": return listMemberships.find((membership) => membership.id === recipient.audienceMembershipId)?.name ?? `Unavailable list membership (${recipient.audienceMembershipId})`;
            case "ActingUser": return "Acting user";
            default: return "Unavailable recipient";
        }
    };
    const statusDescription = (recipient) => !recipient.applicationStatusTypeId ? "All statuses"
        : applicationStatuses.find((status) => status.id === recipient.applicationStatusTypeId)?.name ?? "Unavailable application status";

    return (
        <section className={`clientCard ${styles.eventCard}`} aria-labelledby={`event-${event.eventId}`}>
            <div className="clientCardInner">
                <div className={styles.eventHeader}>
                    <div>
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                            <h4 id={`event-${event.eventId}`} className="mb-0">{event.eventName}</h4>
                            <Badge bg={event.recipients.length ? "success" : "secondary"}>
                                {event.recipients.length ? "Configured" : "Disabled"}
                            </Badge>
                            {isDirty ? <Badge bg="warning" text="dark">Unsaved changes</Badge> : null}
                        </div>
                        <p className="text-muted mb-0">{event.description}</p>
                    </div>
                    {canManage && !isEditing ? <Button variant="outline-primary" onClick={startEditing}>Edit recipients</Button> : null}
                </div>

                {successText ? <Alert variant="success" className="mt-3 mb-0" role="status">{successText}</Alert> : null}
                {errorText ? <Alert variant="danger" className="mt-3 mb-0">{errorText}</Alert> : null}
                {hasRemoteChanges ? <Alert variant="warning" className="mt-3 mb-0">Saved recipients changed while you were editing. Your edits have been kept. Cancel to load the saved recipients before editing again.</Alert> : null}

                {isEditing ? (
                    <Form onSubmit={save} className="mt-3">
                        <fieldset disabled={updateMutation.isPending}>
                            <legend className="visually-hidden">Recipients for {event.eventName}</legend>
                            {recipients.map((recipient, index) => {
                                const selectedUser = usersById.get(recipient.userId);
                                const unavailableUser = recipient.source === "User" && recipient.userId && !selectedUser?.isEligible;
                                const prefix = `recipient-${event.eventId}-${recipient.rowId}`;
                                return (
                                    <div className={`${styles.recipientRow} ${event.supportsApplicationStatusFilter ? styles.withStatus : ""}`} key={recipient.rowId}>
                                        <Form.Group controlId={`${prefix}-delivery`}>
                                            <Form.Label>Delivery</Form.Label>
                                            <Form.Select value={recipient.deliveryType} disabled={Boolean(unavailableUser)} aria-label={`Delivery ${index + 1}`}
                                                onChange={(change) => changeRecipient(recipient.rowId, { deliveryType: change.target.value })}>
                                                {deliveryTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group controlId={`${prefix}-source`}>
                                            <Form.Label>Recipient type</Form.Label>
                                            <Form.Select value={recipient.source} aria-label={`Recipient type ${index + 1}`}
                                                onChange={(change) => changeRecipient(recipient.rowId, { source: change.target.value, userId: null, propertyName: null, audienceMembershipId: null })}>
                                                {event.dynamicRecipients.length ? <option value="ApplicationProperty">Application assignment</option> : null}
                                                <option value="User">Specific user</option>
                                                {listMemberships.length ? <option value="ListMembership">List membership</option> : null}
                                                {event.supportsActingUser ? <option value="ActingUser">Acting user</option> : null}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group controlId={`${prefix}-recipient`}>
                                            <Form.Label>{recipient.source === "User" ? "User" : recipient.source === "ListMembership" ? "List membership" : recipient.source === "ActingUser" ? "Recipient" : "Application property"}</Form.Label>
                                            {recipient.source === "User" ? (
                                                <Form.Select value={recipient.userId ?? ""} aria-label={`User ${index + 1}`}
                                                    onChange={(change) => changeRecipient(recipient.rowId, { userId: change.target.value ? Number(change.target.value) : null })}>
                                                    <option value="">Choose a user</option>
                                                    {unavailableUser ? <option value={recipient.userId} disabled>{userLabel(selectedUser, recipient.userId)} (not eligible)</option> : null}
                                                    {eligibleUsers.map((user) => <option key={user.id} value={user.id}>{userLabel(user)}</option>)}
                                                </Form.Select>
                                            ) : recipient.source === "ApplicationProperty" ? (
                                                <Form.Select value={recipient.propertyName ?? ""} aria-label={`Application property ${index + 1}`}
                                                    onChange={(change) => changeRecipient(recipient.rowId, { propertyName: change.target.value || null })}>
                                                    <option value="">Choose an application property</option>
                                                    {event.dynamicRecipients.map((property) => <option key={property.key} value={property.key}>{property.label}</option>)}
                                                </Form.Select>
                                            ) : recipient.source === "ListMembership" ? (
                                                <Form.Select value={recipient.audienceMembershipId ?? ""} aria-label={`List membership ${index + 1}`}
                                                    onChange={(change) => changeRecipient(recipient.rowId, { audienceMembershipId: change.target.value ? Number(change.target.value) : null })}>
                                                    <option value="">Choose a list membership</option>
                                                    {listMemberships.map((membership) => <option key={membership.id} value={membership.id}>{membership.name}</option>)}
                                                </Form.Select>
                                            ) : <div className="form-control-plaintext">Person who makes the change</div>}
                                            {recipient.source === "ApplicationProperty" ? <Form.Text>Only the person assigned on this application.</Form.Text> : null}
                                            {recipient.source === "ListMembership" ? <Form.Text>All eligible users whose roles include this list membership.</Form.Text> : null}
                                            {recipient.source === "ActingUser" ? <Form.Text>The person who makes the change is included only when configured as a recipient.</Form.Text> : null}
                                            {unavailableUser ? <Form.Text className="text-warning d-block">This user will not receive emails. Replace or remove this recipient to change its delivery or status selection.</Form.Text> : null}
                                            {recipient.source === "User" && !eligibleUsers.length ? <Form.Text>No eligible users are available.</Form.Text> : null}
                                        </Form.Group>
                                        {event.supportsApplicationStatusFilter ? <Form.Group controlId={`${prefix}-status`} className={styles.statusField}>
                                            <Form.Label>Application status</Form.Label>
                                            <Form.Select value={recipient.applicationStatusTypeId ?? ""} disabled={Boolean(unavailableUser)} aria-label={`Application status ${index + 1}`}
                                                onChange={(change) => changeRecipient(recipient.rowId, { applicationStatusTypeId: change.target.value || null })}>
                                                <option value="">All statuses</option>
                                                {recipient.applicationStatusTypeId && !applicationStatuses.some((status) => status.id === recipient.applicationStatusTypeId)
                                                    ? <option value={recipient.applicationStatusTypeId} disabled>Unavailable application status</option> : null}
                                                {applicationStatuses.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}
                                            </Form.Select>
                                        </Form.Group> : null}
                                        <Button type="button" variant="outline-danger" className={styles.removeButton} aria-label={`Remove recipient ${index + 1}`}
                                            onClick={() => {
                                                setDraft((previous) => ({ ...previous, recipients: previous.recipients.filter((item) => item.rowId !== recipient.rowId) }));
                                                setErrorText("");
                                            }}>Remove</Button>
                                    </div>
                                );
                            })}
                            {!recipients.length ? <p className={styles.emptyState}>No recipients. Saving will disable emails for this event.</p> : null}
                            <Button type="button" variant="outline-primary" onClick={addRecipient} disabled={recipients.length >= maxRecipients}>Add recipient</Button>
                            {recipients.length >= maxRecipients ? <small className="text-muted d-block mt-2">Each event supports up to {maxRecipients} recipients.</small> : null}
                            <div className={styles.editorFooter}>
                                <small className="text-muted">Application assignments, list memberships, and account eligibility are checked again before delivery.</small>
                                <div className="d-flex gap-2">
                                    <Button type="button" variant="secondary" onClick={() => { setDraft(null); setErrorText(""); }}>Cancel</Button>
                                    <Button type="submit" variant="primary" disabled={!isDirty || hasRemoteChanges}>
                                        {updateMutation.isPending ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : "Save recipients"}
                                    </Button>
                                </div>
                            </div>
                        </fieldset>
                    </Form>
                ) : !recipients.length ? (
                    <p className={`${styles.emptyState} mt-3 mb-0`}>No recipients configured. Emails for this event are disabled.</p>
                ) : (
                    <dl className={styles.recipientSummary}>
                        {deliveryTypes.map((type) => (
                            <div className={styles.deliveryGroup} key={type.value}>
                                <dt>{type.label}</dt>
                                <dd>
                                    {recipients.filter((recipient) => recipient.deliveryType === type.value).length ? (
                                        <ul className="list-unstyled mb-0">
                                            {recipients.filter((recipient) => recipient.deliveryType === type.value).map((recipient, index) => (
                                                <li key={index}>
                                                    <span>{recipientDescription(recipient)}</span>
                                                    {recipient.source === "ApplicationProperty" ? <small className="text-muted d-block">Assigned user on the application</small> : null}
                                                    {recipient.source === "ListMembership" ? <small className="text-muted d-block">All eligible users whose roles include this list membership</small> : null}
                                                    {recipient.source === "ActingUser" ? <small className="text-muted d-block">Person who makes the change</small> : null}
                                                    {event.supportsApplicationStatusFilter ? <small className="text-muted d-block">Status: {statusDescription(recipient)}</small> : null}
                                                    {recipient.source === "User" && !usersById.get(recipient.userId)?.isEligible ? <small className="text-warning d-block">Not eligible — will not receive emails</small> : null}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <span className="text-muted">None</span>}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}
            </div>
        </section>
    );
};

const EmailNotifications = () => {
    const permissions = useUserStore((state) => state.permissions);
    const canManage = hasPermission(permissions, permissionPaths.administration.manageEmailNotifications);
    const canView = hasAnyPermission(permissions, [permissionPaths.administration.viewEmailNotifications, permissionPaths.administration.manageEmailNotifications]);
    const query = useEmailNotificationsQuery(canView);

    useEffect(() => { document.title = "MFP - Automated Emails"; }, []);

    return (
        <LoggedInLayout>
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <div>
                        <h3>Automated Emails</h3>
                        <Breadcrumb>
                            <Breadcrumb.Item className="primarybreadcrumb">Admin</Breadcrumb.Item>
                            <Breadcrumb.Item className="secondarybreadcrumb" active>Automated Emails</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                    {canView ? <Button variant="outline-primary" onClick={() => query.refetch()} disabled={query.isFetching}>
                        {query.isFetching ? <><Spinner animation="border" size="sm" className="me-2" />Refreshing...</> : "Refresh"}
                    </Button> : null}
                </div>
                {!canView ? <Alert variant="danger">You do not have permission to view automated emails.</Alert> : <>
                    <div className={`dataCard ${styles.introduction}`}>
                        <p>Choose who receives each automated email, including To, CC, and BCC recipients. Email events and message wording are predefined.</p>
                        <p className="text-muted">Select a specific user, a person assigned on the application, or all users in a list membership configured on their roles. Add Acting user to include the person who makes the change. The general application status event lets each recipient apply to all statuses or one selected status. Within an event, all matching rules contribute recipients to the same email.</p>
                        <p className="text-muted">The general application status event and each specific status event are configured independently. Enabling both for the same status can send separate emails.</p>
                        <p className="text-muted">Only active, unlocked users with a valid email address receive these emails. Disable former staff in Manage Users to stop their automated emails.</p>
                        <p className="mb-0 text-muted">Remove every recipient to disable emails for an event.</p>
                    </div>
                    {!canManage ? <Alert variant="info">You have view access. Managing recipients requires the Automated Emails manage permission.</Alert> : null}
                    {query.isError ? <Alert variant="danger">{getApiErrorMessages(query.error, "Unable to load automated emails.").join(" ")} Use Refresh to try again.</Alert> : null}
                    {query.isLoading ? <div className="text-center py-5" role="status"><Spinner animation="border" size="sm" className="me-2" />Loading email events...</div> : null}
                    {query.data?.events.length === 0 ? <Alert variant="info">No automated email events are available.</Alert> : null}
                    {(query.data?.events ?? []).map((event) => <EmailEventCard key={event.eventId} event={event} users={query.data.users ?? []} listMemberships={query.data.listMemberships ?? []} canManage={canManage} />)}
                </>}
            </div>
        </LoggedInLayout>
    );
};

export default EmailNotifications;
